import { useSearchParams } from 'react-router-dom';
import { useCallback, useMemo } from 'react';

export type AppSearchParams = {
  offset?: string; // "0"
  limit?: string; // "25"
  q?: string; // "filename:ilike:%samp%"
  sort?: string; // "filename"
  direction?: 'asc' | 'desc';
  // Allow additional filter parameters
  [key: string]: string | undefined;
};

export function useNewAppSearchParams() {
  const [sp, setSp] = useSearchParams();

  const all = useMemo(
    () => Object.fromEntries(sp.entries()) as AppSearchParams,
    [sp]
  );

  const update = useCallback(
    (patch: Partial<AppSearchParams>) => {
      const next: Record<string, string> = { ...all } as Record<string, string>;

      Object.entries(patch).forEach(([k, v]) => {
        if (v === undefined || v === null || v === '') {
          delete next[k];
        } else {
          next[k] = String(v);
        }
      });

      setSp(next, { replace: true });
    },
    [all, setSp]
  );

  const setSearchParams = useCallback(
    (patch: Partial<AppSearchParams>) => update(patch),
    [update]
  );

  const setSorting = useCallback(
    (field?: string, dir?: 'asc' | 'desc') => {
      if (!field || !dir) {
        update({ sort: undefined, direction: undefined });
      } else {
        update({ sort: field, direction: dir });
      }
    },
    [update]
  );

  return {
    searchParams: all,
    setSearchParams,
    setSorting
  };
}
